import React, { useMemo, useState } from 'react';
import { Checkbox, Empty, Flex, Input, Spin, theme } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { FilterDropdownProps } from 'antd/lib/table/interface';

import WidgetFooter from './WidgetFooter';

type DomainDropdownFilterProps = Pick<FilterDropdownProps, 'confirm' | 'clearFilters'> & {
  selectedKeys: React.Key[];
  setSelectedKeys: (keys: React.Key[]) => void;
  domains: string[];
  loading?: boolean;
};

function DomainDropdownFilter({
  confirm,
  clearFilters,
  selectedKeys,
  setSelectedKeys,
  domains,
  loading = false,
}: DomainDropdownFilterProps) {
  const { token } = theme.useToken();
  const [search, setSearch] = useState('');

  const selected = selectedKeys as string[];
  const normalizedSearch = search.trim().toLowerCase();

  // Selected domains stay pinned at the top and visible regardless of the
  // search term; the search only narrows the remaining (unselected) domains.
  const unselectedMatches = useMemo(
    () =>
      domains.filter(
        (domain) => !selected.includes(domain) && domain.toLowerCase().includes(normalizedSearch)
      ),
    [domains, selected, normalizedSearch]
  );

  const toggleDomain = (domain: string) => {
    setSelectedKeys(
      selected.includes(domain) ? selected.filter((value) => value !== domain) : [...selected, domain]
    );
  };

  // Commit the current selection when the dropdown closes (click-away), mirroring
  // the other column filters (e.g. StatusDropdownFilter).
  React.useEffect(() => {
    return () => confirm();
  }, []);

  const showNoMatches = !loading && normalizedSearch.length > 0 && unselectedMatches.length === 0;

  return (
    <Flex vertical style={{ padding: token.paddingSM, width: 240 }} gap={token.marginXS}>
      <Input
        aria-label="Search domains"
        prefix={<SearchOutlined />}
        placeholder="Search domains"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        allowClear
      />

      <Flex vertical gap={token.marginXXS} style={{ maxHeight: 240, overflowY: 'auto' }}>
        {selected.map((domain) => (
          <Checkbox key={domain} checked onChange={() => toggleDomain(domain)}>
            {domain}
          </Checkbox>
        ))}

        {loading ? (
          <Flex justify="center" style={{ padding: token.paddingSM }}>
            <Spin size="small" />
          </Flex>
        ) : (
          unselectedMatches.map((domain) => (
            <Checkbox key={domain} checked={false} onChange={() => toggleDomain(domain)}>
              {domain}
            </Checkbox>
          ))
        )}

        {showNoMatches && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No domains found" />}
      </Flex>

      <WidgetFooter onReset={() => clearFilters?.()} onOk={() => confirm()} resetDisabled={selected.length === 0} />
    </Flex>
  );
}

export default DomainDropdownFilter;
